import {takeLatest, put} from 'redux-saga/effects';
import * as userActionTypes from '../actions/userActionTypes';
import {DocumentResponse} from '../../utils/underwritingRequirementUtil';
import {FirstIdentificationType} from 'lib/entities';
import maskSSN from 'lib/utils/mask';

type MultiPartUpdateAction = {
    type: string;
    payload: any;
    callback?: any;
    secondaryPayload: any;
    file: File;
};

function* updateUserAsync(action: Action) {
    try {
        const res: Response = yield updateUserAPI(JSON.stringify(action.payload));
        if (res.status === 200) {
            yield put({
                type: userActionTypes.USER_UPDATE_USER_ASYNC,
                payload: action.payload,
                callback: action.callback,
            });
        } else {
            yield put({type: userActionTypes.USER_UPDATE_USER_FAILED, message: 'Update failed'});
        }
    } catch (e: unknown) {
        yield put({type: userActionTypes.USER_UPDATE_USER_FAILED, message: (e as Error).message});
    }
}

function* createIdentificationAsync(action: Action) {
    try {
        if (action.payload.firstIdentification != null) {
            const res: Response = yield createIdentificationAPI(
                JSON.stringify({
                    customer_id: action.payload.customer_id,
                    type: action.payload.firstIdentification.type,
                    value: JSON.stringify(action.payload.firstIdentification.value),
                }),
            );
            if (res.status !== 200) {
                // do some error handling
                yield put({type: userActionTypes.USER_CREATE_IDENTIFICATION_FAILED, message: 'Update failed'});
                return;
            }
            if (
                [
                    FirstIdentificationType.US_CITIZEN,
                    FirstIdentificationType.PERMANENT_RESIDENT_NUMBER,
                    FirstIdentificationType.US_VISA,
                ].includes(action.payload.firstIdentification.type) &&
                action.payload.firstIdentification.value.socialSecurityNumber
            ) {
                const maskedSsn: string = yield maskSSN(action.payload.firstIdentification.value.socialSecurityNumber);
                action.payload.firstIdentification.value.socialSecurityNumber = maskedSsn;
            }
        }
        if (action.payload.secondIdentification != null) {
            const res: Response = yield createIdentificationAPI(
                JSON.stringify({
                    customer_id: action.payload.customer_id,
                    type: action.payload.secondIdentification.type,
                    value: JSON.stringify(action.payload.secondIdentification.value),
                }),
            );
            if (res.status !== 200) {
                // do some error handling
                yield put({type: userActionTypes.USER_CREATE_IDENTIFICATION_FAILED, message: 'Update failed'});
                return;
            }
        }
        yield put({
            type: userActionTypes.USER_CREATE_IDENTIFICATION_ASYNC,
            payload: action.payload,
            callback: action.callback,
        });
    } catch (e: unknown) {
        yield put({type: userActionTypes.USER_CREATE_IDENTIFICATION_FAILED, message: (e as Error).message});
    }
}
function* updateIdentificationAsync(action: Action) {
    try {
        if (action.payload.firstIdentification != null) {
            const res: Response = yield updateIdentificationAPI(
                JSON.stringify({
                    customer_id: action.payload.customer_id,
                    type: action.payload.firstIdentification.type,
                    value: JSON.stringify(action.payload.firstIdentification.value),
                }),
            );
            if (res.status !== 200) {
                // do some error handling
                yield put({type: userActionTypes.USER_UPDATE_IDENTIFICATION_FAILED, message: 'Update failed'});
                return;
            }
            if (
                [
                    FirstIdentificationType.US_CITIZEN,
                    FirstIdentificationType.PERMANENT_RESIDENT_NUMBER,
                    FirstIdentificationType.US_VISA,
                ].includes(action.payload.firstIdentification.type) &&
                action.payload.firstIdentification.value.socialSecurityNumber
            ) {
                const maskedSsn: string = yield maskSSN(action.payload.firstIdentification.value.socialSecurityNumber);
                action.payload.firstIdentification.value.socialSecurityNumber = maskedSsn;
            }
        }
        if (action.payload.secondIdentification != null) {
            const res: Response = yield createIdentificationAPI(
                JSON.stringify({
                    customer_id: action.payload.customer_id,
                    type: action.payload.secondIdentification.type,
                    value: JSON.stringify(action.payload.secondIdentification.value),
                }),
            );
            if (res.status !== 200) {
                // do some error handling
                yield put({type: userActionTypes.USER_UPDATE_IDENTIFICATION_FAILED, message: 'Update failed'});
                return;
            }
        }
        yield put({
            type: userActionTypes.USER_UPDATE_IDENTIFICATION_ASYNC,
            payload: action.payload,
            callback: action.callback,
        });
    } catch (e: unknown) {
        yield put({type: userActionTypes.USER_UPDATE_IDENTIFICATION_FAILED, message: (e as Error).message});
    }
}

function* createEventAsync(action: Action) {
    try {
        const res: Response = yield createEventAPI(
            JSON.stringify({
                customer_id: action.payload.customer_id,
                date: action.payload.date,
                type: action.payload.type,
            }),
        );
        if (res.status !== 200) {
            // do some error handling
            yield put({type: userActionTypes.USER_CREATE_ADVISOR_CALL_EVENT_FAILED, message: 'Update failed'});
            return;
        }
        const hubspotRes: Response = yield createHubspotRecordCallAPI(
            JSON.stringify({
                email: action.payload.email,
                time: action.payload.date,
            }),
        );
        if (hubspotRes.status != 200) {
            // do some error handling
            yield put({type: userActionTypes.USER_CREATE_ADVISOR_CALL_EVENT_FAILED, message: 'Update failed'});
            return;
        }

        yield put({
            type: userActionTypes.USER_CREATE_ADVISOR_CALL_EVENT_ASYNC,
            payload: action.payload,
            callback: action.callback,
        });
    } catch (e: unknown) {
        yield put({type: userActionTypes.USER_CREATE_ADVISOR_CALL_EVENT_FAILED, message: (e as Error).message});
    }
}

function* createPaymentInformation(action: Action) {
    try {
        const res: Response = yield createPaymentInformationAPI(JSON.stringify(action.payload));
        if (res.status !== 200) {
            yield put({type: userActionTypes.USER_CREATE_PAYMENT_INFORMATION_FAILED, message: 'Add failed'});
            return;
        }

        yield put({
            type: userActionTypes.USER_CREATE_PAYMENT_INFORMATION_ASYNC,
            payload: action.payload,
            callback: action.callback,
        });
    } catch (e: unknown) {
        yield put({type: userActionTypes.USER_CREATE_PAYMENT_INFORMATION_FAILED, message: (e as Error).message});
    }
}

function* updateUnderwritingRequirement(action: Action) {
    try {
        const res: Response = yield createRequirementUpdateAPI(action.payload);

        if (res.status !== 200) {
            yield put({
                type: userActionTypes.USER_UPDATE_UNDERWRITING_REQUIREMENT_FAILED,
                message: 'Requirement update failed',
                callback: action.callback,
            });
            return;
        }
        yield put({
            type: userActionTypes.USER_UPDATE_UNDERWRITING_REQUIREMENT_ASYNC,
            payload: action.payload,
            callback: action.callback,
        });
    } catch (error: unknown) {
        yield put({
            type: userActionTypes.USER_UPDATE_UNDERWRITING_REQUIREMENT_FAILED,
            message: (error as Error).message,
            callback: action.callback,
        });
    }
}

const createRequirementStatusUpdateAPI = (params: any) => {
    const apiUrl = '/api/update/requirement/status';
    const paramsString = JSON.stringify(params);
    return fetch(apiUrl, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: paramsString,
    });
};

function* createUnderwritingDocument(action: MultiPartUpdateAction) {
    try {
        const res: Response = yield createDocumentUpdateAPI(action.payload);
        if (res.status !== 200) {
            throw new Error('Underwriting document creation failed');
        }
        const documentResponse: DocumentResponse = yield res.json();
        // Wait for file to upload successfully
        yield uploadToS3(documentResponse.uploadUrl, action.file);
        yield put({
            type: userActionTypes.USER_UPDATE_UNDERWRITING_DOCUMENT_ASYNC,
            payload: action.payload,
            callback: action.callback,
        });
        // Update the requirement
        yield put({
            type: userActionTypes.USER_UPDATE_UNDERWRITING_REQUIREMENT,
            payload: action.secondaryPayload,
            callback: action.callback,
        });
    } catch (error: unknown) {
        yield put({
            type: userActionTypes.USER_UPDATE_UNDERWRITING_DOCUMENT_FAILED,
            payload: action.payload,
            message: (error as Error).message,
        });
    }
}

function* createEmploymentInformation(action: Action) {
    try {
        const res: Response = yield requirementCreateAPI(JSON.stringify(action.payload));
        if (res.status !== 200) {
            yield put({
                type: userActionTypes.USER_CREATE_EMPLOYMENT_INFO_FAILED,
                message: 'Failed to save employment information',
                callback: action.callback,
            });
            return;
        }
        yield put({
            type: userActionTypes.USER_CREATE_EMPLOYMENT_INFO_ASYNC,
            payload: action.payload,
            callback: action.callback,
        });
    } catch (error: unknown) {
        yield put({type: userActionTypes.USER_CREATE_EMPLOYMENT_INFO_FAILED, message: (error as Error).message});
    }
}
function* updateEmploymentInformation(action: Action) {
    try {
        const res: Response = yield requirementUpdateAPI(JSON.stringify(action.payload));
        if (res.status !== 200) {
            yield put({
                type: userActionTypes.USER_UPDATE_EMPLOYMENT_INFO_FAILED,
                message: 'Failed to update employment information',
                callback: action.callback,
            });
            return;
        }
        yield put({
            type: userActionTypes.USER_UPDATE_EMPLOYMENT_INFO_ASYNC,
            payload: action.payload,
            callback: action.callback,
        });
    } catch (error: unknown) {
        yield put({type: userActionTypes.USER_UPDATE_EMPLOYMENT_INFO_FAILED, message: (error as Error).message});
    }
}

function* updateUserConsent(action: Action) {
    try {
        const res: Response = yield updateUserConsentAPI(JSON.stringify(action.payload));
        if (res.status !== 200) {
            yield put({
                type: userActionTypes.USER_UPDATE_CONSENT_FAILED,
                message: 'Failed up to update consent for user',
            });
        }
        yield put({
            type: userActionTypes.USER_UPDATE_CONSENT_ASYNC,
            payload: action.payload,
            callback: action.callback,
        });
    } catch (error: unknown) {
        yield put({type: userActionTypes.USER_UPDATE_CONSENT_FAILED, message: (error as Error).message});
    }
}

const updateUserAPI = (params: string) => {
    return fetch('/api/updateUser', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: params,
    });
};

const updateUserConsentAPI = (params: string) => {
    return fetch('/api/createConsent', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: params,
    });
};

const createIdentificationAPI = (params: string) => {
    return fetch('/api/createIdentification', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: params,
    });
};
const updateIdentificationAPI = (params: string) => {
    return fetch('/api/updateIdentification', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: params,
    });
};

const createEventAPI = (params: string) => {
    return fetch('/api/createEvent', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: params,
    });
};

const createHubspotRecordCallAPI = (params: string) => {
    return fetch('/api/hubspotRecordCall', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: params,
    });
};

const createPaymentInformationAPI = (params: string) => {
    return fetch('/api/createPaymentInformation/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: params,
    });
};

const createRequirementUpdateAPI = (params: any) => {
    const apiUrl = params.llt ? '/api/update/requirement' : '/api/updateRequirement';
    const paramsString = JSON.stringify(params);
    return fetch(apiUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: paramsString,
    });
};

const createDocumentUpdateAPI = (params: any) => {
    const apiUrl = params.llt ? '/api/update/document' : '/api/createDocument';
    const paramsString = JSON.stringify(params);
    return fetch(apiUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: paramsString,
    });
};

/**
 * Currently only used for Employment Information
 * TODO: Investigate consolidation of `/api/requirements` post method
 *
 * @param params
 */
const requirementCreateAPI = (params: string) => {
    return fetch('/api/createEmploymentInfo/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: params,
    });
};
const requirementUpdateAPI = (params: string) => {
    return fetch('/api/updateEmploymentInfo/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: params,
    });
};

async function uploadToS3(uploadUrl: string, requirementFile: File) {
    const resp = await fetch(uploadUrl, {
        method: 'PUT',
        body: requirementFile,
    });

    if (resp.ok) return resp;

    throw new Error(`Underwriting document creation failed due to: {${resp.status}, ${resp.text() || 'Unknown'}}`);
}

export default function* userSaga() {
    yield takeLatest(userActionTypes.USER_UPDATE_USER, updateUserAsync);
    yield takeLatest(userActionTypes.USER_CREATE_IDENTIFICATION, createIdentificationAsync);
    yield takeLatest(userActionTypes.USER_UPDATE_IDENTIFICATION, updateIdentificationAsync);
    yield takeLatest(userActionTypes.USER_CREATE_ADVISOR_CALL_EVENT, createEventAsync);
    yield takeLatest(userActionTypes.USER_CREATE_PAYMENT_INFORMATION, createPaymentInformation);
    yield takeLatest(userActionTypes.USER_UPDATE_UNDERWRITING_REQUIREMENT, updateUnderwritingRequirement);
    yield takeLatest(userActionTypes.USER_UPDATE_UNDERWRITING_DOCUMENT, createUnderwritingDocument);
    yield takeLatest(userActionTypes.USER_UPDATE_CONSENT, updateUserConsent);
    yield takeLatest(userActionTypes.USER_CREATE_EMPLOYMENT_INFO, createEmploymentInformation);
    yield takeLatest(userActionTypes.USER_UPDATE_EMPLOYMENT_INFO, updateEmploymentInformation);
}
